import { CSRF_COOKIE, CSRF_HEADER, type ApiErrorBody, type ApiErrorCode } from '@tbbt/shared';
import { env } from '@/config/env';

/**
 * The single HTTP client.
 *
 * Everything the app sends goes through here, which is what makes three cross-cutting
 * concerns solvable in one place instead of at every call site: credential handling,
 * CSRF token echoing, and turning a failed response into a typed error.
 */

/**
 * A failed request, carrying the server's machine-readable code and field errors.
 *
 * Extending `Error` keeps it compatible with everything that expects one (React Query,
 * error boundaries, `console.error`), while `code` lets calling code branch on the
 * specific failure rather than string-matching a message that may be reworded.
 */
export class ApiError extends Error {
  readonly statusCode: number;
  readonly code: ApiErrorCode;
  readonly fieldErrors: Record<string, string[]>;
  readonly requestId: string;

  constructor(body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.statusCode = body.statusCode;
    this.code = body.code;
    this.fieldErrors = body.fieldErrors ?? {};
    this.requestId = body.requestId;
  }

  /** True when the user is not (or is no longer) authenticated. */
  get isUnauthenticated(): boolean {
    return this.statusCode === 401;
  }

  get isForbidden(): boolean {
    return this.statusCode === 403;
  }

  get isNotFound(): boolean {
    return this.statusCode === 404;
  }

  /** True when the user must set a new password before doing anything else. */
  get requiresPasswordChange(): boolean {
    return this.code === 'PASSWORD_CHANGE_REQUIRED';
  }

  /** Field errors flattened for `react-hook-form`'s `setError`. */
  fieldError(path: string): string | undefined {
    return this.fieldErrors[path]?.[0];
  }
}

/** Read a non-httpOnly cookie. Only the CSRF token is readable by design. */
function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;

  const match = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${encodeURIComponent(name)}=`));

  return match ? decodeURIComponent(match.slice(match.indexOf('=') + 1)) : undefined;
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Serialised as JSON unless it is already a `FormData` (file upload). */
  body?: unknown;
  /** Appended as a query string, skipping null and undefined values. */
  query?: Record<string, string | number | boolean | undefined | null>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = `${env.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return base;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    // Omitting empty values keeps URLs clean and lets the server's schema defaults
    // apply, rather than sending `?status=` and having it parsed as an empty string.
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }

  const queryString = params.toString();
  return queryString ? `${base}?${queryString}` : base;
}

/**
 * Refresh-in-flight guard.
 *
 * When an access token expires, several queries typically fail with 401 at once. Without
 * this, each would fire its own refresh, and because refresh tokens rotate, the second
 * request would present a token the first had already spent — which the server correctly
 * treats as a replay and responds to by revoking the whole session. Sharing one in-flight
 * refresh across all waiters is what prevents a routine token expiry from logging the
 * user out.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      // Cleared in a microtask so concurrent callers all observe the same promise
      // before it is reset.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

/** Called when a session cannot be recovered, so the app can redirect to sign-in. */
type SessionExpiredHandler = () => void;
let onSessionExpired: SessionExpiredHandler | null = null;

export function setSessionExpiredHandler(handler: SessionExpiredHandler): void {
  onSessionExpired = handler;
}

async function parseError(response: Response, path: string): Promise<ApiError> {
  try {
    const body = (await response.json()) as ApiErrorBody;
    // Trust the server's envelope when it is well formed.
    if (typeof body.code === 'string' && typeof body.message === 'string') {
      return new ApiError(body);
    }
  } catch {
    // Fall through: a proxy error page or a network failure is not JSON.
  }

  return new ApiError({
    statusCode: response.status,
    code: 'INTERNAL',
    message:
      response.status === 0
        ? 'Could not reach the server. Check your connection.'
        : `Request failed with status ${response.status}.`,
    requestId: response.headers.get('x-request-id') ?? 'unknown',
    timestamp: new Date().toISOString(),
    path,
  });
}

async function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
  isRetry = false,
): Promise<T> {
  const { body, query, headers, ...rest } = options;
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const requestHeaders = new Headers(headers);
  // `Accept: application/json` also tells the SPA host not to answer with the HTML
  // shell when a path does not exist.
  requestHeaders.set('Accept', 'application/json');

  if (body !== undefined && !isFormData) {
    requestHeaders.set('Content-Type', 'application/json');
  }

  // Echo the readable CSRF cookie back as a header — the double-submit half the
  // server verifies. See CsrfGuard.
  if (MUTATING_METHODS.has(method)) {
    const csrfToken = readCookie(CSRF_COOKIE);
    if (csrfToken) requestHeaders.set(CSRF_HEADER, csrfToken);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      ...rest,
      method,
      headers: requestHeaders,
      // Required for the session cookies to be sent at all.
      credentials: 'include',
      body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
    });
  } catch {
    // A network-level failure (offline, DNS, CORS) never reaches the server, so there
    // is no envelope to parse — synthesise one so callers have a single error shape.
    throw new ApiError({
      statusCode: 0,
      code: 'INTERNAL',
      message: 'Could not reach the server. Check your connection and try again.',
      requestId: 'unknown',
      timestamp: new Date().toISOString(),
      path,
    });
  }

  /**
   * On a 401, try exactly one refresh-and-retry.
   *
   * `isRetry` bounds it to a single attempt, so an endpoint that returns 401 even with
   * a valid session cannot cause an infinite loop. The refresh endpoint itself is
   * excluded for the same reason.
   */
  if (response.status === 401 && !isRetry && !path.startsWith('/auth/')) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return request<T>(method, path, options, true);
    }
    onSessionExpired?.();
  }

  if (!response.ok) {
    throw await parseError(response, path);
  }

  // 204 No Content has no body to parse.
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return (await response.text()) as T;
  }

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions): Promise<T> => request<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('POST', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('PUT', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('PATCH', path, { ...options, body }),
  delete: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>('DELETE', path, options),
};
